import Supercluster, { ClusterFeature, PointFeature } from 'supercluster';
import { ScraperEntry } from './importScraper';

// Define types
export type MapLocation = {
  lat: number;
  lng: number;
  entry: ScraperEntry;
  id: string;
};

export type MarkerClickHandler = (locationId: string, position: google.maps.LatLngLiteral) => void;

// Properties for individual points
interface PointProperties {
  locationId: string;
  forkScore: number;
  name: string;
}

// Properties that Supercluster adds to clusters
interface ClusterProperties {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string;
  // We'll calculate average fork score from leaves
}

type GeoJSONPoint = PointFeature<PointProperties>;
type SuperclusterResult = ClusterFeature<ClusterProperties> | PointFeature<PointProperties>;

// Animation configuration
const ANIMATION_DURATION = 150; // ms
const ANIMATION_EASING = 'ease-out';

// Options for creating markers with specific animation states
interface CreateMarkerOptions {
  startHidden?: boolean;
}

export class SuperclusterManager {
  private map: google.maps.Map;
  private index: Supercluster<PointProperties, ClusterProperties>;
  private pointMarkers: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map();
  // Use position-based keys for clusters (stable across index rebuilds)
  private clusterMarkers: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map();
  private clusterCounts: Map<string, number> = new Map(); // Track cluster counts for updates
  private clusterIdsByKey: Map<string, number> = new Map(); // Map position key to current clusterId
  private locations: MapLocation[] = [];
  private onMarkerClick: MarkerClickHandler | null = null;
  private idleListener: google.maps.MapsEventListener | null = null;
  private zoomListener: google.maps.MapsEventListener | null = null;
  // Track pending removals to cancel if marker reappears quickly
  private pendingPointRemovals: Map<string, NodeJS.Timeout> = new Map();
  private pendingClusterRemovals: Map<string, NodeJS.Timeout> = new Map();

  constructor(map: google.maps.Map) {
    this.map = map;
    this.index = new Supercluster({
      radius: 60,      // Cluster radius in pixels
      maxZoom: 16,     // Max zoom to cluster at
      minPoints: 2,    // Min points to form cluster
    });

    // Listen for map changes to update visible clusters
    this.idleListener = map.addListener('idle', () => {
      this.renderVisibleClusters();
    });

    this.zoomListener = map.addListener('zoom_changed', () => {
      // On zoom change, we need to re-render as clustering changes
      this.renderVisibleClusters();
    });
  }

  /**
   * Set a handler for marker click events
   */
  setMarkerClickHandler(handler: MarkerClickHandler): void {
    this.onMarkerClick = handler;
  }

  /**
   * Update the locations data and refresh the display
   */
  updateLocations(locations: MapLocation[]): void {
    this.locations = locations;

    // Convert to GeoJSON features for Supercluster
    const points: GeoJSONPoint[] = locations.map(loc => ({
      type: 'Feature' as const,
      properties: {
        locationId: loc.id,
        forkScore: this.calculateForkScore(loc),
        name: loc.entry.name,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [loc.lng, loc.lat],
      },
    }));

    // Load points into the spatial index
    this.index.load(points);

    // Render what's visible
    this.renderVisibleClusters();
  }

  /**
   * Render clusters and points for the current viewport
   * This is the key method that implements incremental updates
   */
  renderVisibleClusters(): void {
    // Skip if no data has been loaded yet
    if (this.locations.length === 0) return;

    const bounds = this.map.getBounds();
    const zoom = this.map.getZoom();
    if (!bounds || zoom === undefined) return;

    // Get bounding box for Supercluster query with 25% padding
    // Padding ensures markers near edges (with labels ~150px wide) don't disappear abruptly
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const lngPadding = (ne.lng() - sw.lng()) * 0.25;
    const latPadding = (ne.lat() - sw.lat()) * 0.25;

    const bbox: [number, number, number, number] = [
      sw.lng() - lngPadding,
      sw.lat() - latPadding,
      ne.lng() + lngPadding,
      ne.lat() + latPadding,
    ];

    // Get clusters and points for the current viewport
    const clusters = this.index.getClusters(bbox, Math.floor(zoom));

    // Track what should be visible
    const visiblePointIds = new Set<string>();
    const visibleClusterKeys = new Set<string>();

    // Process each result
    clusters.forEach(feature => {
      if (this.isCluster(feature)) {
        // It's a cluster - use position-based key for stable tracking
        const [lng, lat] = feature.geometry.coordinates;
        const clusterId = feature.properties.cluster_id;
        const count = feature.properties.point_count;

        // Generate stable position-based key (rounded to ~10m precision)
        const clusterKey = `cluster_${Math.round(lat * 10000)}_${Math.round(lng * 10000)}`;
        visibleClusterKeys.add(clusterKey);

        const existingMarker = this.clusterMarkers.get(clusterKey);
        const existingCount = this.clusterCounts.get(clusterKey);

        // Cancel any pending removal if marker reappears
        if (existingMarker) {
          this.cancelPendingRemoval(clusterKey, existingMarker, this.pendingClusterRemovals);
        }

        if (!existingMarker) {
          // Create new cluster marker
          const marker = this.createClusterMarker(feature);
          this.clusterMarkers.set(clusterKey, marker);
          this.clusterCounts.set(clusterKey, count);
          this.clusterIdsByKey.set(clusterKey, clusterId);
        } else if (existingCount !== count) {
          // Cluster count changed - animate the transition
          this.animateClusterCountChange(existingMarker, clusterKey, feature, clusterId, count);
        } else {
          // Position and count same - just update the clusterId mapping
          this.clusterIdsByKey.set(clusterKey, clusterId);
        }
        // If count is same, keep existing marker (no flash!)
      } else {
        // It's an individual point
        const locationId = feature.properties.locationId;
        visiblePointIds.add(locationId);

        const existingMarker = this.pointMarkers.get(locationId);

        // Cancel any pending removal if marker reappears
        if (existingMarker) {
          this.cancelPendingRemoval(locationId, existingMarker, this.pendingPointRemovals);
        }

        if (!existingMarker) {
          // Create new point marker
          const location = this.locations.find(l => l.id === locationId);
          if (location) {
            const marker = this.createPointMarker(location);
            this.pointMarkers.set(locationId, marker);
          }
        }
        // If marker exists, keep it (no flash!)
      }
    });

    // Remove point markers that are no longer visible (with animation)
    this.pointMarkers.forEach((marker, id) => {
      if (!visiblePointIds.has(id)) {
        this.scheduleRemoval(id, marker, this.pendingPointRemovals, () => {
          this.pointMarkers.delete(id);
        });
      }
    });

    // Remove cluster markers that are no longer visible (with animation)
    this.clusterMarkers.forEach((marker, key) => {
      if (!visibleClusterKeys.has(key)) {
        this.scheduleRemoval(key, marker, this.pendingClusterRemovals, () => {
          this.clusterMarkers.delete(key);
          this.clusterCounts.delete(key);
          this.clusterIdsByKey.delete(key);
        });
      }
    });
  }

  /**
   * Type guard to check if a feature is a cluster
   */
  private isCluster(feature: SuperclusterResult): feature is ClusterFeature<ClusterProperties> {
    return 'cluster' in feature.properties && feature.properties.cluster === true;
  }

  /**
   * Animate a marker out with fade and scale, then execute cleanup
   */
  private animateMarkerOut(
    marker: google.maps.marker.AdvancedMarkerElement,
    onComplete: () => void
  ): void {
    const content = marker.content as HTMLElement;
    if (content) {
      content.style.transition = `opacity ${ANIMATION_DURATION}ms ${ANIMATION_EASING}, transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
      content.style.opacity = '0';
      content.style.transform = 'scale(0.5)';

      setTimeout(() => {
        marker.map = null;
        onComplete();
      }, ANIMATION_DURATION);
    } else {
      marker.map = null;
      onComplete();
    }
  }

  /**
   * Cancel a pending removal and restore marker visibility
   */
  private cancelPendingRemoval(
    key: string,
    marker: google.maps.marker.AdvancedMarkerElement,
    pendingMap: Map<string, NodeJS.Timeout>
  ): void {
    const pending = pendingMap.get(key);
    if (pending) {
      clearTimeout(pending);
      pendingMap.delete(key);
      // Restore marker visibility if it was fading
      const content = marker.content as HTMLElement;
      if (content) {
        content.style.opacity = '1';
        content.style.transform = 'scale(1)';
      }
    }
  }

  /**
   * Schedule a marker removal with animation, allowing cancellation if it reappears
   */
  private scheduleRemoval(
    key: string,
    marker: google.maps.marker.AdvancedMarkerElement,
    pendingMap: Map<string, NodeJS.Timeout>,
    cleanupFn: () => void
  ): void {
    // Cancel any existing pending removal for this key
    const existing = pendingMap.get(key);
    if (existing) {
      clearTimeout(existing);
    }

    // Schedule new removal with small delay to allow cancellation
    const timeout = setTimeout(() => {
      this.animateMarkerOut(marker, () => {
        cleanupFn();
        pendingMap.delete(key);
      });
    }, 50); // Small delay allows cancellation if marker reappears quickly

    pendingMap.set(key, timeout);
  }

  /**
   * Animate cluster count change with cross-fade
   */
  private animateClusterCountChange(
    existingMarker: google.maps.marker.AdvancedMarkerElement,
    clusterKey: string,
    newCluster: ClusterFeature<ClusterProperties>,
    clusterId: number,
    newCount: number
  ): void {
    // Create new marker (starts hidden for cross-fade)
    const newMarker = this.createClusterMarker(newCluster, { startHidden: true });

    // Fade out old marker
    const oldContent = existingMarker.content as HTMLElement;
    if (oldContent) {
      oldContent.style.transition = `opacity ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
      oldContent.style.opacity = '0';
    }

    // Fade in new marker
    const newContent = newMarker.content as HTMLElement;
    if (newContent) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          newContent.style.opacity = '1';
          newContent.style.transform = 'scale(1)';
        });
      });
    }

    // Clean up old marker after animation
    setTimeout(() => {
      existingMarker.map = null;
    }, ANIMATION_DURATION);

    // Update tracking
    this.clusterMarkers.set(clusterKey, newMarker);
    this.clusterCounts.set(clusterKey, newCount);
    this.clusterIdsByKey.set(clusterKey, clusterId);
  }

  /**
   * Calculate fork score for a location
   */
  private calculateForkScore(location: MapLocation): number {
    const forks = Number(location.entry.inspectionForks);
    if (!isNaN(forks) && forks >= 0 && forks <= 5) {
      return forks;
    }
    return 0;
  }

  /**
   * Get color based on fork score
   */
  private getColorForScore(score: number): string {
    if (score > 4.5) return "#228B22"; // Forest Green (best)
    if (score > 3.5) return "#355E3B"; // Dark Green
    if (score > 2.5) return "#317873"; // Teal
    if (score > 1.5) return "#F59E0B"; // Orange
    if (score > 0)   return "#DC2626"; // Red (worst)
    return "#6B7280"; // Gray (no data)
  }

  /**
   * Calculate average fork score for a cluster
   */
  private getAverageScoreForCluster(clusterId: number): number {
    try {
      const leaves = this.index.getLeaves(clusterId, Infinity);
      if (leaves.length === 0) return 0;

      let totalScore = 0;
      let validCount = 0;

      leaves.forEach(leaf => {
        const score = leaf.properties.forkScore;
        if (typeof score === 'number' && !isNaN(score)) {
          totalScore += score;
          validCount++;
        }
      });

      return validCount > 0 ? totalScore / validCount : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Create a cluster marker
   * @param options.startHidden - If true, marker starts hidden for cross-fade animation
   */
  private createClusterMarker(
    cluster: ClusterFeature<ClusterProperties>,
    options: CreateMarkerOptions = {}
  ): google.maps.marker.AdvancedMarkerElement {
    const [lng, lat] = cluster.geometry.coordinates;
    const count = cluster.properties.point_count;
    const clusterId = cluster.properties.cluster_id;

    // Calculate average fork score for coloring
    const averageScore = this.getAverageScoreForCluster(clusterId);
    const color = this.getColorForScore(averageScore);

    // Determine size based on count
    let size = 34;
    if (count > 10) size = 42;
    if (count > 50) size = 52;
    if (count > 100) size = 64;

    const w = size + 8;
    const cx = w / 2;
    const fontSize = size <= 34 ? 13 : size <= 42 ? 15 : size <= 52 ? 17 : 20;

    // Clean solid circle with white border and subtle outer glow
    const svg = window.btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${w}" width="${w}" height="${w}">
        <circle cx="${cx}" cy="${cx}" r="${size / 2 + 3}" fill="${color}" opacity="0.15" />
        <circle cx="${cx}" cy="${cx}" r="${size / 2}" fill="${color}" stroke="#fff" stroke-width="2" />
        <text x="50%" y="50%" text-anchor="middle" dy=".35em" style="fill:#fff;font-size:${fontSize}px;font-weight:600;font-family:-apple-system,system-ui,sans-serif;">${count.toLocaleString()}</text>
      </svg>`);

    const clusterIcon = document.createElement("img");
    clusterIcon.src = `data:image/svg+xml;base64,${svg}`;
    clusterIcon.style.width = `${size}px`;
    clusterIcon.style.height = `${size}px`;
    clusterIcon.style.cursor = "pointer";
    clusterIcon.style.filter = "drop-shadow(0 2px 6px rgba(0,0,0,0.35))";

    // Set up animation - either hidden for cross-fade or animated entrance
    clusterIcon.style.opacity = "0";
    clusterIcon.style.transform = options.startHidden ? "scale(0.8)" : "scale(0.5)";
    clusterIcon.style.transition = `opacity ${ANIMATION_DURATION}ms ${ANIMATION_EASING}, transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;

    // Only auto-animate if not starting hidden (cross-fade handles its own animation)
    if (!options.startHidden) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          clusterIcon.style.opacity = "1";
          clusterIcon.style.transform = "scale(1)";
        });
      });
    }

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: { lat, lng },
      content: clusterIcon,
      zIndex: 10 + count,
    });

    // Store position key for click handling (position is stable, clusterId is not)
    const clusterKey = `cluster_${Math.round(lat * 10000)}_${Math.round(lng * 10000)}`;
    (marker as any).clusterKey = clusterKey;

    // Add click handler to zoom into cluster
    marker.addListener("click", (event: google.maps.MapMouseEvent) => {
      event.stop();
      // Look up current clusterId from the position key
      const currentClusterId = this.clusterIdsByKey.get(clusterKey);
      if (currentClusterId !== undefined) {
        this.onClusterClick(currentClusterId);
      }
    });

    return marker;
  }

  /**
   * Handle cluster click - zoom to expand
   */
  private onClusterClick(clusterId: number): void {
    try {
      // Get the leaves to calculate bounds for responsive padding
      const leaves = this.index.getLeaves(clusterId, Infinity);
      if (leaves.length === 0) return;

      const bounds = new google.maps.LatLngBounds();

      leaves.forEach(leaf => {
        const [lng, lat] = leaf.geometry.coordinates;
        bounds.extend({ lat, lng });
      });

      // Responsive padding calculation
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobile = viewportWidth < 768;
      const isSmallScreen = viewportWidth < 640;

      let padding: google.maps.Padding;

      if (isSmallScreen) {
        const basePadding = leaves.length < 5 ? 50 : 80;
        padding = {
          top: Math.min(basePadding, viewportHeight * 0.1),
          bottom: Math.min(basePadding, viewportHeight * 0.1),
          left: Math.min(basePadding, viewportWidth * 0.1),
          right: Math.min(basePadding, viewportWidth * 0.1)
        };
      } else if (isMobile) {
        const basePadding = leaves.length < 5 ? 80 : 120;
        padding = {
          top: Math.min(basePadding, viewportHeight * 0.15),
          bottom: Math.min(basePadding, viewportHeight * 0.15),
          left: Math.min(basePadding, viewportWidth * 0.1),
          right: Math.min(basePadding, viewportWidth * 0.1)
        };
      } else {
        const basePadding = leaves.length < 5 ? 100 : 200;
        padding = {
          top: basePadding,
          bottom: basePadding,
          left: basePadding,
          right: basePadding
        };
      }

      this.map.fitBounds(bounds, padding);

      // Cap zoom for small clusters on mobile
      if (isMobile && leaves.length < 3) {
        setTimeout(() => {
          const currentZoom = this.map.getZoom();
          if (currentZoom !== undefined && currentZoom > 16) {
            this.map.setZoom(16);
          }
        }, 100);
      }
    } catch (e) {
      console.error("Error expanding cluster:", e);
    }
  }

  /**
   * Create a point marker for an individual location
   */
  private createPointMarker(location: MapLocation): google.maps.marker.AdvancedMarkerElement {
    const position = { lat: location.lat, lng: location.lng };
    const forkScore = this.calculateForkScore(location);
    const forkScoreColor = this.getColorForScore(forkScore);

    // Create the visible inner circle
    const markerCircle = document.createElement("div");
    markerCircle.style.width = "18px";
    markerCircle.style.height = "18px";
    markerCircle.style.borderRadius = "50%";
    markerCircle.style.backgroundColor = forkScoreColor;
    markerCircle.style.border = "2px solid rgba(255, 255, 255, 0.8)";
    markerCircle.style.boxShadow = "0 1px 3px rgba(0,0,0,0.2)";
    markerCircle.style.transition = "transform 0.1s ease-out";

    // Create the label element
    const markerLabel = document.createElement("div");
    markerLabel.className = "map-marker-label";
    markerLabel.textContent = location.entry.name;
    markerLabel.style.position = "absolute";
    markerLabel.style.left = "30px";
    markerLabel.style.top = "15px";
    markerLabel.style.padding = "3px 6px";
    markerLabel.style.borderRadius = "4px";
    markerLabel.style.fontSize = "11px";
    markerLabel.style.fontWeight = "600";
    markerLabel.style.maxWidth = "150px";
    markerLabel.style.overflow = "hidden";
    markerLabel.style.textOverflow = "ellipsis";
    markerLabel.style.whiteSpace = "nowrap";
    markerLabel.style.zIndex = "1";
    markerLabel.style.cursor = "pointer";
    markerLabel.style.setProperty("--marker-accent", `${forkScoreColor}20`);
    markerLabel.style.transform = "translateY(-50%)";
    markerLabel.style.display = "block";

    // Create wrapper for click area
    const markerWrapper = document.createElement("div");
    markerWrapper.style.width = "30px";
    markerWrapper.style.height = "30px";
    markerWrapper.style.display = "flex";
    markerWrapper.style.justifyContent = "center";
    markerWrapper.style.alignItems = "center";
    markerWrapper.style.cursor = "pointer";
    markerWrapper.style.backgroundColor = "transparent";
    markerWrapper.style.position = "relative";

    markerWrapper.appendChild(markerCircle);
    markerWrapper.appendChild(markerLabel);

    // Animate new markers in
    markerWrapper.style.opacity = "0";
    markerWrapper.style.transform = "scale(0.5)";
    markerWrapper.style.transition = "opacity 150ms ease-out, transform 150ms ease-out";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        markerWrapper.style.opacity = "1";
        markerWrapper.style.transform = "scale(1)";
      });
    });

    // Hover effects
    markerWrapper.onmouseover = () => {
      markerCircle.style.transform = "scale(1.2)";
    };
    markerWrapper.onmouseout = () => {
      markerCircle.style.transform = "scale(1)";
    };

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position,
      content: markerWrapper,
      title: location.entry.name,
      zIndex: 1,
      gmpClickable: false,
    });

    // Add click listener
    marker.addListener("click", (event: google.maps.MapMouseEvent) => {
      event.stop();
      if (this.onMarkerClick) {
        this.onMarkerClick(location.id, position);
      }
    });

    return marker;
  }

  /**
   * Force a full re-render of all markers (e.g. after theme/colorScheme change)
   */
  forceRefresh(): void {
    this.clearMarkers();
    this.renderVisibleClusters();
  }

  /**
   * Clear all markers from the map
   */
  clearMarkers(): void {
    this.pointMarkers.forEach(marker => {
      marker.map = null;
    });
    this.pointMarkers.clear();

    this.clusterMarkers.forEach(marker => {
      marker.map = null;
    });
    this.clusterMarkers.clear();
    this.clusterCounts.clear();
    this.clusterIdsByKey.clear();
  }

  /**
   * Get a marker by its ID
   */
  getMarker(id: string): google.maps.marker.AdvancedMarkerElement | undefined {
    return this.pointMarkers.get(id);
  }

  /**
   * Check if a marker with the given ID exists
   */
  hasMarker(id: string): boolean {
    return this.pointMarkers.has(id);
  }

  /**
   * Get all marker IDs
   */
  getMarkerIds(): string[] {
    return Array.from(this.pointMarkers.keys());
  }

  /**
   * Get the count of point markers
   */
  getMarkerCount(): number {
    return this.pointMarkers.size;
  }

  /**
   * Clean up listeners when done
   */
  destroy(): void {
    if (this.idleListener) {
      google.maps.event.removeListener(this.idleListener);
    }
    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
    }
    // Clean up pending removal timeouts
    this.pendingPointRemovals.forEach(timeout => clearTimeout(timeout));
    this.pendingPointRemovals.clear();
    this.pendingClusterRemovals.forEach(timeout => clearTimeout(timeout));
    this.pendingClusterRemovals.clear();
    this.clearMarkers();
  }
}

export default SuperclusterManager;
